import React from "react";
import { Instagram, Linkedin, Globe, Mail } from "lucide-react";
import { circuloDe } from "@/lib/directoryConfig";

/**
 * AttendeeCard — "quién es quién" card inside the private directory.
 */
const AttendeeCard = ({ attendee, llave, isMe = false }) => {
  const api = process.env.REACT_APP_BACKEND_URL;
  const photoSrc = `${api}${attendee.photo_url}?llave=${encodeURIComponent(llave)}`;
  const circulo = circuloDe(attendee.punto_partida);

  const canalHref = (() => {
    if (!attendee.canal_tipo || !attendee.canal_usuario) return null;
    const user = attendee.canal_usuario.trim();
    if (/^https?:\/\//i.test(user)) return user;
    const clean = user.replace(/^@/, "");
    return attendee.canal_tipo === "instagram"
      ? `https://instagram.com/${clean}`
      : `https://linkedin.com/in/${clean}`;
  })();

  return (
    <article
      className={`bg-[#FAF8F3] border ${isMe ? "border-[#63846F]" : "border-[#3F403A]/10"} flex flex-col`}
      data-testid={`attendee-card-${attendee.id}`}
    >
      <div className="relative overflow-hidden bg-[#F4EFE7]" style={{ aspectRatio: "4/5" }}>
        <img
          src={photoSrc}
          alt={`Foto de ${attendee.nombre}`}
          loading="lazy"
          className="h-full w-full object-cover"
          data-testid={`attendee-photo-${attendee.id}`}
        />
        {circulo && (
          <span className="absolute top-3 left-3 bg-[#3F403A]/85 text-[#FAF8F3] overline text-[10px] px-2.5 py-1">
            Círculo {circulo}
          </span>
        )}
        {isMe && (
          <span
            className="absolute top-3 right-3 bg-[#63846F] text-[#FAF8F3] overline text-[10px] px-2.5 py-1"
            data-testid="attendee-me-badge"
          >
            Tu ficha
          </span>
        )}
      </div>

      <div className="p-6 md:p-7 flex flex-col flex-1">
        <h3 className="font-serif text-2xl leading-tight text-[#3F403A]">{attendee.nombre}</h3>
        <p className="mt-1.5 overline text-[11px] text-[#63846F]">{attendee.dedicacion}</p>

        <dl className="mt-6 space-y-4 text-sm font-sans text-[#3F403A]/85 leading-relaxed">
          <div>
            <dt className="overline text-[10px] text-[#3F403A]/50 mb-1">Soluciona mejor que nadie</dt>
            <dd>{attendee.solucionas}</dd>
          </div>
          <div>
            <dt className="overline text-[10px] text-[#3F403A]/50 mb-1">Puede aportar a la sala</dt>
            <dd>{attendee.aportas}</dd>
          </div>
          <div>
            <dt className="overline text-[10px] text-[#3F403A]/50 mb-1">Busca en esta edición</dt>
            <dd>{attendee.buscas}</dd>
          </div>
          <div>
            <dt className="overline text-[10px] text-[#3F403A]/50 mb-1">Para romper el hielo</dt>
            <dd className="italic">{attendee.curiosidad}</dd>
          </div>
        </dl>

        <div className="mt-6 pt-5 border-t border-[#3F403A]/10 flex items-center gap-4">
          <a
            href={`mailto:${attendee.email}`}
            aria-label={`Email de ${attendee.nombre}`}
            className="text-[#3F403A]/60 hover:text-[#63846F] transition-colors"
            data-testid={`attendee-email-${attendee.id}`}
          >
            <Mail className="h-4.5 w-4.5 h-[18px] w-[18px]" strokeWidth={1.5} />
          </a>
          {canalHref && (
            <a
              href={canalHref}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`${attendee.canal_tipo} de ${attendee.nombre}`}
              className="text-[#3F403A]/60 hover:text-[#63846F] transition-colors"
              data-testid={`attendee-canal-${attendee.id}`}
            >
              {attendee.canal_tipo === "instagram" ? (
                <Instagram className="h-[18px] w-[18px]" strokeWidth={1.5} />
              ) : (
                <Linkedin className="h-[18px] w-[18px]" strokeWidth={1.5} />
              )}
            </a>
          )}
          {attendee.web && (
            <a
              href={/^https?:\/\//i.test(attendee.web) ? attendee.web : `https://${attendee.web}`}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Web de ${attendee.nombre}`}
              className="text-[#3F403A]/60 hover:text-[#63846F] transition-colors"
              data-testid={`attendee-web-${attendee.id}`}
            >
              <Globe className="h-[18px] w-[18px]" strokeWidth={1.5} />
            </a>
          )}
        </div>
      </div>
    </article>
  );
};

export default AttendeeCard;
